// User directory + chat-bootstrap endpoints. Two canonical surfaces
// (see openapi.yaml on openmls.ping.live):
//
//   GET  /v1/users/search?q=<query>&limit=<n>
//        → 200 { users: UserSearchHit[] }
//        Substring `display_name` match across discoverable accounts.
//        Used by contacts / calls suggestion lists, NOT the new-chat
//        modal (which needs exact 3PID resolution).
//
//   GET  /v1/users/lookup?identifier=<email|E.164>
//        → 200 { user: UserSearchHit } | 404
//        Exact match on `auth.accounts.primary_3pid`. Cheap probe;
//        does NOT claim a KeyPackage from the target's pool.
//
//   POST /v1/contacts/lookup { username: <email|E.164> }
//        → 200 { account_id, device_id, key_package }
//        Atomic resolve-and-claim. This is the trigger for actually
//        starting a chat — `addMembers([key_package])` needs the
//        returned bytes, and `setWelcomeRecipient(...)` needs
//        device_id so the Welcome envelope carries `?recipient=`.
//
// `POST /v1/conversations/request` from earlier iterations is gone
// — `POST /v1/contacts/lookup` is the start-of-chat trigger now.
//
// All identity-adjacent fields (display name, etc.) come from the
// server — we do not cache or persist them in this module.

import {
  bytesToHex,
  bytesToUrlB64NoPad,
  canonicalDeviceIdHex,
  hexToBytes,
  urlB64NoPadToBytes,
} from '@ping/ui';

import { get, NetworkError, post } from './api';
import { WITH_MOCK } from './mockFlag';

/** One sibling device awaiting admission (GET /v1/devices/pending-admissions). */
export interface PendingAdmissionEntry {
  readonly deviceId: string;
  readonly historyDeliveryPubkey?: string;
}

/**
 * Server-coordinated linking ledger drain: sibling devices of this account that
 * linked and still need admission to the account's existing conversations. Any
 * online sibling reads this and admits each one (idempotently). Returns [] on
 * error — best-effort, retried on the next load. Never triggers global reauth.
 */
export async function fetchPendingAdmissions(): Promise<PendingAdmissionEntry[]> {
  try {
    const resp = await get<{
      devices?: Array<{ device_id: string; history_delivery_pubkey?: string }>;
    }>('/v1/devices/pending-admissions', { headers: { 'X-Ping-Skip-Reauth': '1' } });
    return (resp.devices ?? []).map((d) => ({
      deviceId: d.device_id,
      ...(d.history_delivery_pubkey ? { historyDeliveryPubkey: d.history_delivery_pubkey } : {}),
    }));
  } catch {
    return [];
  }
}

export interface DirectoryUser {
  readonly id: string;
  readonly username: string;
  readonly displayName: string;
  readonly role?: string | undefined;
  readonly company?: string | undefined;
  readonly avatarUri?: string | undefined;
  readonly presence?: 'online' | 'away' | 'busy' | 'offline' | undefined;
  /** 3PID contact badge (verified email/phone) — the blue check. */
  readonly verified?: boolean | undefined;
  /** KYC/identity-verified badge. */
  readonly identityVerified?: boolean | undefined;
}

/**
 * Resolve a media id to a fetchable download URL (`GET /v1/media/{id}`),
 * matching how the chat list resolves peer avatars. Returns `undefined` on any
 * failure so a missing avatar degrades to initials rather than throwing. The
 * search/lookup mappers MUST do this — `<Avatar imageUri>` needs a URL, not the
 * raw media id the directory returns.
 */
export async function resolveMediaUrl(
  mediaId: string,
  signal?: AbortSignal,
): Promise<string | undefined> {
  try {
    const opts: { signal?: AbortSignal; headers?: Record<string, string> } = {
      headers: { 'X-Ping-Skip-Reauth': '1' },
      ...(signal ? { signal } : {}),
    };
    const r = await get<{ download_url: string }>(
      `/v1/media/${encodeURIComponent(mediaId)}`,
      opts,
    );
    return r.download_url.length > 0 ? r.download_url : undefined;
  } catch {
    return undefined;
  }
}

export interface SearchUsersResponse {
  readonly users: readonly DirectoryUser[];
}

// `UserSearchHit` from openapi.yaml is `{ account_id, display_name,
// avatar_media_id? }`. We map it to `DirectoryUser` at the network
// boundary so the rest of the app keeps working with the existing
// shape (`id`, `username`, `displayName`, …).
interface UserSearchHitWire {
  readonly account_id: string;
  readonly display_name: string;
  readonly avatar_media_id?: string | null;
  readonly verified?: boolean | null;
  readonly identity_verified?: boolean | null;
}

interface UserLookupResponseWire {
  readonly user: UserSearchHitWire;
}

export interface UserLookupResult {
  readonly user: DirectoryUser | null;
}

// `POST /v1/contacts/lookup` response — atomic resolve + KP claim.
// `account_id` is base64url 16 bytes, `device_id` is base64url 32
// bytes, `key_package` is base64url-encoded MLS KeyPackage bytes.
interface ContactsLookupWire {
  readonly account_id: string;
  readonly device_id: string;
  readonly key_package: string;
}

export interface ClaimedContact {
  readonly accountId: string;
  readonly deviceId: string;
  readonly keyPackage: Uint8Array;
}

/**
 * Multi-device claim result — one entry per active device of the
 * target account. Per docs/architecture/multi-device.md "Per-device
 * fan-out": every device is its own MLS leaf, so a chat between two
 * users with 2 and 3 devices is a 5-leaf MLS group.
 */
export interface ClaimedContactDevice {
  readonly deviceId: string;
  readonly keyPackage: Uint8Array;
}

export interface ClaimedContactMultiDevice {
  readonly accountId: string;
  readonly devices: readonly ClaimedContactDevice[];
}

// Dev-only fixtures. Lifted from the Figma "Contacts main screens"
// frame so what the developer sees mirrors the design exactly.
// Production builds tree-shake this constant out because the
// `WITH_MOCK` guards return earlier.
const DEV_MOCK_USERS: readonly DirectoryUser[] = [
  {
    id: 'u_arthur',
    username: 'arthur.blackwood',
    displayName: 'Arthur Blackwood',
    role: 'Software Engineer',
    company: 'Zenith Dynamics',
    presence: 'online',
  },
  {
    id: 'u_alessia',
    username: 'alessia.harrell',
    displayName: 'Alessia Harrell',
    role: 'Technical Lead',
    company: 'Omega Group',
    presence: 'online',
  },
  {
    id: 'u_alice',
    username: 'alice.parsons',
    displayName: 'Alice Parsons',
    role: 'DevOps Engineer',
    company: 'Visionary Tech',
    presence: 'away',
  },
  {
    id: 'u_judy',
    username: 'judy.booker',
    displayName: 'Judy Booker',
    role: 'Security Analyst',
    company: 'Optimum Systems',
    presence: 'online',
  },
  {
    id: 'u_archie',
    username: 'archie.sterling',
    displayName: 'Archie Sterling',
    role: 'Data Scientist',
    company: 'Nova Innovations',
    presence: 'offline',
  },
  {
    id: 'u_abigail',
    username: 'abigail.stone',
    displayName: 'Abigail Stone',
    role: 'Marketing Analyst',
    company: 'Spectrum Group',
    presence: 'online',
  },
  {
    id: 'u_billy',
    username: 'billy.bobson',
    displayName: 'Billy Bobson',
    role: 'Sales Representative',
    company: 'Pinnacle Corp.',
    presence: 'busy',
  },
  {
    id: 'u_bobby',
    username: 'bobby.billions',
    displayName: 'Bobby Billions',
    role: 'Financial Analyst',
    company: 'Pinnacle Tech',
    presence: 'online',
  },
  {
    id: 'u_tanisha',
    username: 'tanisha.downs',
    displayName: 'Tanisha Downs',
    role: 'QA Engineer',
    company: 'Alpha Dynamics',
    presence: 'online',
  },
  {
    id: 'u_alex',
    username: 'alex.lombardi',
    displayName: 'Alex Lombardi',
    role: 'Product Manager',
    company: 'Apex Systems',
    presence: 'online',
  },
];

function devLocalMatch(query: string): readonly DirectoryUser[] {
  const q = query.trim().toLowerCase();
  if (q.length === 0) return DEV_MOCK_USERS;
  return DEV_MOCK_USERS.filter(
    (u) =>
      u.displayName.toLowerCase().includes(q) ||
      u.username.toLowerCase().includes(q) ||
      (u.company?.toLowerCase().includes(q) ?? false) ||
      (u.role?.toLowerCase().includes(q) ?? false),
  );
}

function isBackendMissing(err: unknown): boolean {
  return err instanceof NetworkError && (err.status === 404 || err.status === 501);
}

// Wire shape from openapi.yaml `UserSearchResponse`: snake_case keys.
// The exported `DirectoryUser` is camelCase, so we MUST transform —
// returning the raw wire as `DirectoryUser` (which used to happen
// here) left every consumer reading `u.id` / `u.displayName` as
// `undefined`, which broke React keys and the avatar fallback in the
// global search modal.
interface UserSearchResponseWire {
  readonly users: readonly UserSearchHitWire[];
}

export async function searchUsers(params: {
  query: string;
  limit?: number;
  signal?: AbortSignal;
  /**
   * Best-effort/decorative lookups (avatar resolution, presence, mention
   * typeahead) pass `true` so a 401 here NEVER triggers the global reauth
   * → token wipe → logout. A directory miss should degrade to initials /
   * empty results, not sign the user out.
   */
  skipReauth?: boolean;
}): Promise<SearchUsersResponse> {
  const limit = params.limit ?? 20;
  try {
    const qs = new URLSearchParams({
      q: params.query,
      limit: String(limit),
    });
    const opts: { signal?: AbortSignal; headers?: Record<string, string> } = {
      ...(params.signal ? { signal: params.signal } : {}),
      ...(params.skipReauth === true ? { headers: { 'X-Ping-Skip-Reauth': '1' } } : {}),
    };
    const wire = await get<UserSearchResponseWire>(`/v1/users/search?${qs}`, opts);
    return {
      users: await Promise.all(
        wire.users.map((hit) => hitToDirectoryUser(hit, params.signal)),
      ),
    };
  } catch (err) {
    if (WITH_MOCK && (isBackendMissing(err) || !(err instanceof NetworkError))) {
      // Dev only: keep the typeahead exercisable when the BE hasn't
      // shipped yet (or when the dev server is offline).
      return { users: devLocalMatch(params.query).slice(0, limit) };
    }
    if (isBackendMissing(err)) {
      // Prod build, BE not yet deployed → empty result (UI shows
      // the "No people match" empty state).
      return { users: [] };
    }
    throw err;
  }
}

async function hitToDirectoryUser(
  hit: UserSearchHitWire,
  signal?: AbortSignal,
): Promise<DirectoryUser> {
  // The directory returns a media id; resolve it to a fetchable URL so the
  // search row's <Avatar> can actually load the picture.
  const avatarUri = hit.avatar_media_id
    ? await resolveMediaUrl(hit.avatar_media_id, signal)
    : undefined;
  return {
    id: hit.account_id,
    // The /v1/users/search response carries no separate `username`
    // field. We expose the account_id as `username` so downstream
    // start-chat flows have a stable identifier to thread through.
    username: hit.account_id,
    displayName: hit.display_name,
    ...(avatarUri ? { avatarUri } : {}),
    ...(hit.verified === true ? { verified: true } : {}),
    ...(hit.identity_verified === true ? { identityVerified: true } : {}),
  };
}

/**
 * Resolve an active account by exact email or E.164 phone.
 * Returns `{ user: null }` on 404 so callers can render an empty
 * state without try/catching. All other errors propagate.
 *
 * Inputs that don't yet look like a complete 3PID (partial typeahead
 * like `+` or `+90`) skip the network call entirely — the BE
 * responds 404 to anything that doesn't exactly match a primary 3PID
 * and the network panel fills with red entries on every keystroke
 * otherwise.
 */
export async function lookupUser(params: {
  identifier: string;
  signal?: AbortSignal;
}): Promise<UserLookupResult> {
  const trimmed = params.identifier.trim();
  if (trimmed.length === 0) {
    return { user: null };
  }
  if (!looksLikeCompleteIdentifier(trimmed)) {
    return { user: null };
  }
  try {
    const qs = new URLSearchParams({ identifier: trimmed });
    const opts = params.signal ? { signal: params.signal } : {};
    const resp = await get<UserLookupResponseWire>(
      `/v1/users/lookup?${qs.toString()}`,
      opts,
    );
    const avatarUri = resp.user.avatar_media_id
      ? await resolveMediaUrl(resp.user.avatar_media_id, params.signal)
      : undefined;
    return {
      user: {
        id: resp.user.account_id,
        // OpenAPI doesn't carry a `username` separate from the 3PID —
        // surface what the user typed so the list row still has a
        // secondary line, matching the `DirectoryUser` shape.
        username: trimmed,
        displayName: resp.user.display_name,
        ...(avatarUri ? { avatarUri } : {}),
        ...(resp.user.verified === true ? { verified: true } : {}),
        ...(resp.user.identity_verified === true ? { identityVerified: true } : {}),
      },
    };
  } catch (err) {
    if (err instanceof NetworkError && err.status === 404) {
      return { user: null };
    }
    throw err;
  }
}

// ── Peer profile (chatroom profile popover + full modal) ──────────────
//
//   GET /v1/users/{account_id}/profile
//     → 200 UserProfileResponse | 404
//   Peer-visible profile for an account the caller shares an active
//   conversation with. Returns the rich display fields (bio, job, company,
//   contact, timezone, status, verified). Avatar is a media id — the caller
//   resolves it to a URL via the media download endpoint.

export interface PeerProfileStatus {
  readonly emoji?: string | undefined;
  readonly text: string;
  /** Epoch ms. */
  readonly expiresAt?: number | undefined;
}

export interface PeerProfile {
  readonly accountId: string;
  readonly displayName: string;
  readonly avatarMediaId?: string | undefined;
  /** Presigned, directly-fetchable avatar download URL when the backend rode it
   *  on the response (kills the per-member `GET /v1/media/{id}` roster N+1). When
   *  absent (older backend / media unwired), the caller resolves `avatarMediaId`
   *  itself. Short-lived (see the media presign TTL) — key it by `avatarMediaId`. */
  readonly avatarUrl?: string | undefined;
  readonly bio?: string | undefined;
  readonly jobTitle?: string | undefined;
  readonly companyName?: string | undefined;
  readonly contactEmail?: string | undefined;
  readonly contactPhone?: string | undefined;
  readonly timezone?: string | undefined;
  readonly status?: PeerProfileStatus | undefined;
  /** 3PID contact badge — NOT the identity (KYC) badge. */
  readonly verified: boolean;
  /** Identity (KYC) verified — drives the verified checkmark badge. */
  readonly identityVerified: boolean;
}

interface UserProfileResponseWire {
  readonly account_id: string;
  readonly display_name: string;
  readonly avatar_media_id?: string | null;
  readonly avatar_url?: string | null;
  readonly avatar_url_expires_at?: number | null;
  readonly bio?: string | null;
  readonly job_title?: string | null;
  readonly company_name?: string | null;
  readonly contact_email?: string | null;
  readonly contact_phone?: string | null;
  readonly timezone?: string | null;
  readonly status?: { emoji?: string | null; text: string; expires_at?: number | null } | null;
  readonly verified: boolean;
  readonly identity_verified?: boolean | null;
}

function mapUserProfileWire(w: UserProfileResponseWire): PeerProfile {
  return {
    accountId: w.account_id,
    displayName: w.display_name,
    ...(w.avatar_media_id ? { avatarMediaId: w.avatar_media_id } : {}),
    ...(w.avatar_url ? { avatarUrl: w.avatar_url } : {}),
    ...(w.bio ? { bio: w.bio } : {}),
    ...(w.job_title ? { jobTitle: w.job_title } : {}),
    ...(w.company_name ? { companyName: w.company_name } : {}),
    ...(w.contact_email ? { contactEmail: w.contact_email } : {}),
    ...(w.contact_phone ? { contactPhone: w.contact_phone } : {}),
    ...(w.timezone ? { timezone: w.timezone } : {}),
    ...(w.status
      ? {
          status: {
            ...(w.status.emoji ? { emoji: w.status.emoji } : {}),
            text: w.status.text,
            ...(w.status.expires_at ? { expiresAt: w.status.expires_at } : {}),
          },
        }
      : {}),
    verified: w.verified === true,
    identityVerified: w.identity_verified === true,
  };
}

/** Hex device ids are not directory account ids — skip before the BE 400s. */
function isFetchableAccountId(accountId: string): boolean {
  const trimmed = accountId.trim();
  return trimmed.length > 0 && !/^[0-9a-fA-F]{32,}$/.test(trimmed);
}

export async function getUserProfile(accountId: string): Promise<PeerProfile | null> {
  const trimmed = accountId.trim();
  if (!isFetchableAccountId(trimmed)) return null;
  try {
    // Decorative read — never let a 401 here trigger the global reauth/logout.
    const w = await get<UserProfileResponseWire>(
      `/v1/users/${encodeURIComponent(trimmed)}/profile`,
      { headers: { 'X-Ping-Skip-Reauth': '1' } },
    );
    return mapUserProfileWire(w);
  } catch (err) {
    if (err instanceof NetworkError && err.status === 404) return null;
    throw err;
  }
}

/** Cap matches OpenAPI `BatchUserProfilesRequest.account_ids` maxItems. */
const MAX_BATCH_PROFILES = 100;

interface BatchUserProfilesResponseWire {
  readonly profiles: readonly UserProfileResponseWire[];
}

/**
 * Batch peer profiles via POST /v1/users/profiles.
 * Returns a map keyed by account id; unauthorised / unknown ids are absent.
 * Chunks requests of >100 ids. Prefer this over N× getUserProfile for rosters.
 */
export async function getUserProfiles(
  accountIds: readonly string[],
): Promise<ReadonlyMap<string, PeerProfile>> {
  const unique: string[] = [];
  const seen = new Set<string>();
  for (const raw of accountIds) {
    const trimmed = raw.trim();
    if (!isFetchableAccountId(trimmed) || seen.has(trimmed)) continue;
    seen.add(trimmed);
    unique.push(trimmed);
  }
  const out = new Map<string, PeerProfile>();
  if (unique.length === 0) return out;

  for (let i = 0; i < unique.length; i += MAX_BATCH_PROFILES) {
    const chunk = unique.slice(i, i + MAX_BATCH_PROFILES);
    try {
      const w = await post<BatchUserProfilesResponseWire>(
        '/v1/users/profiles',
        { account_ids: chunk },
        { headers: { 'X-Ping-Skip-Reauth': '1' } },
      );
      for (const row of w.profiles ?? []) {
        const mapped = mapUserProfileWire(row);
        out.set(mapped.accountId, mapped);
      }
    } catch (err) {
      // Decorative roster enrichment — never fail the open path on a batch miss.
      if (err instanceof NetworkError && (err.status === 404 || err.status === 400)) {
        continue;
      }
      throw err;
    }
  }
  return out;
}

/**
 * The per-ACCOUNT roster of a conversation: the 16-byte directory account ids
 * (base64url) of its active members, from `GET /v1/conversations/{id}/members`.
 * This is the authoritative, broadcast-INDEPENDENT way to learn who is in a
 * conversation — the MLS roster a client holds exposes only the 32-byte
 * `UserId` (a different id space the directory can't resolve), so a freshly
 * linked / late-joining device otherwise depends on the best-effort
 * `ping.profile` re-send to learn a peer's account id.
 *
 * `conversationIdHex` is the local hex group id; the server keys on
 * base64url(16 bytes). Returns `[]` on 404 / not-a-member (403) / bad id.
 */
/**
 * The server's AUTHORITATIVE verdict on this device's membership of a
 * conversation (`GET /v1/conversations/{id}/members`):
 *   - `member`     → 200; you're an active member (roster returned).
 *   - `not_member` → 403 `not_a_member` / 404; the server DENIES membership.
 *                    This is definitive ("this conversation is not yours"), so a
 *                    caller may orphan the local row — a fresh device holding a
 *                    stale MLS group the server never admitted (the ghost "New
 *                    chat") lands here.
 *   - `unknown`    → 401 (token not ready yet) / network / 5xx / bad id: a
 *                    TRANSIENT failure that says nothing about membership; never
 *                    act on it, just retry on the next hydration pass.
 */
export type ConversationMembership =
  | {
      readonly kind: 'member';
      readonly accountIds: readonly string[];
      /**
       * Authoritative device→account map (canonical device hex → 16-byte account
       * id), built from the enriched `member_devices` field. Lets the client
       * resolve a group message's sender (its `sender_device`) WITHOUT a per-member
       * `GET /v1/devices/{account}` fan-out. Empty against an older backend that
       * doesn't yet return `member_devices` — callers fall back to the per-member
       * device lookup in that case.
       */
      readonly deviceAccounts: Readonly<Record<string, string>>;
    }
  | { readonly kind: 'not_member' }
  | { readonly kind: 'unknown' };

interface MemberDeviceWire {
  readonly device_id?: unknown;
  readonly account_id?: unknown;
}

export async function fetchConversationMembership(
  conversationIdHex: string,
): Promise<ConversationMembership> {
  let idB64Url: string;
  try {
    idB64Url = bytesToUrlB64NoPad(hexToBytes(conversationIdHex));
  } catch {
    return { kind: 'unknown' };
  }
  if (idB64Url.length === 0) return { kind: 'unknown' };
  try {
    const resp = await get<{ members?: string[]; member_devices?: MemberDeviceWire[] }>(
      `/v1/conversations/${encodeURIComponent(idB64Url)}/members`,
      { headers: { 'X-Ping-Skip-Reauth': '1' } },
    );
    const accountIds = Array.isArray(resp.members)
      ? resp.members.filter((m): m is string => typeof m === 'string' && m.length > 0)
      : [];
    // Fold the enriched device→account pairs into a canonical-hex-keyed map. The
    // device id arrives base64url (BE `EncodeB64URL`); `canonicalDeviceIdHex`
    // normalizes it to the same lowercase hex the roster maps + message
    // `sender_device` use, so lookups match.
    const deviceAccounts: Record<string, string> = {};
    if (Array.isArray(resp.member_devices)) {
      for (const entry of resp.member_devices) {
        if (typeof entry?.device_id !== 'string' || typeof entry.account_id !== 'string') {
          continue;
        }
        if (entry.account_id.length === 0) continue;
        const hex = canonicalDeviceIdHex(entry.device_id);
        if (hex !== null) deviceAccounts[hex] = entry.account_id;
      }
    }
    return { kind: 'member', accountIds, deviceAccounts };
  } catch (err) {
    // AUTHORITATIVE denial: 403 (`not_a_member`) or 404 (conversation absent).
    if (err instanceof NetworkError && (err.status === 404 || err.status === 403)) {
      return { kind: 'not_member' };
    }
    // Transient (401 token-not-ready, CORS, timeout, 5xx) — say nothing.
    return { kind: 'unknown' };
  }
}

/**
 * Thin adapter over {@link fetchConversationMembership} for callers that only
 * need the roster and treat any non-200 as "no roster". Returns `[]` for
 * `not_member` / `unknown` — callers that must act on the AUTHORITATIVE
 * not-a-member verdict should use `fetchConversationMembership` directly.
 */
export async function fetchConversationMemberAccountIds(
  conversationIdHex: string,
): Promise<readonly string[]> {
  const m = await fetchConversationMembership(conversationIdHex);
  return m.kind === 'member' ? m.accountIds : [];
}

/**
 * Atomic resolve-and-claim. Same 3PID input as `lookupUser`, but
 * also returns one fresh `key_package` from the target device's pool
 * — call this when the user actually confirms the chat. The BE
 * consumes the KeyPackage on the same round-trip, so a successful
 * response is the commit point.
 *
 * 410 (pool empty) propagates as `NetworkError(http, 410)` so the
 * caller can surface "we couldn't reach that person right now".
 */
export async function claimContact(params: {
  identifier: string;
}): Promise<ClaimedContact> {
  const trimmed = params.identifier.trim();
  if (trimmed.length === 0) {
    throw new Error('claimContact: empty identifier');
  }
  const resp = await post<ContactsLookupWire>('/v1/contacts/lookup', {
    username: trimmed,
  });
  return {
    accountId: resp.account_id,
    deviceId: resp.device_id,
    keyPackage: base64urlToBytes(resp.key_package),
  };
}

/**
 * Start-chat helper keyed by the target's `account_id` rather than
 * their primary 3PID. `GET /v1/users/search` doesn't expose the 3PID,
 * only `{ account_id, display_name }`, so when a person comes from
 * the global search results we resolve their device + KeyPackage via
 * the canonical device-discovery endpoint instead:
 *
 *   GET /v1/devices/{user_id}
 *     → { devices: [{ device_id, key_package, last_seen_at }, ...] }
 *
 * The first device wins (single-device DM); multi-device fan-out is
 * a later wave. Note: this path is non-atomic vs. `POST /v1/contacts/lookup`
 * — the BE doesn't pre-consume the KeyPackage here, so two callers
 * racing on the same device can briefly receive the same KP. The
 * server still rejects the second `addMembers` use on Welcome upload,
 * so the outcome is "second caller sees an error" rather than a
 * silent decryption failure. Use `claimContact` (3PID path) when the
 * caller actually has an email or phone number on hand.
 */
interface DeviceDiscoverResponseWire {
  readonly devices: ReadonlyArray<{
    readonly device_id: string;
    readonly key_package: string;
    readonly last_seen_at: number;
    // Present only when the device advertised its history-delivery X25519
    // pubkey (the no-QR recovery signal). base64url, 32 bytes.
    readonly history_delivery_pubkey?: string | null;
  }>;
}

/** One account device that advertised a history-delivery pubkey. */
export interface DeviceWithDeliveryKey {
  readonly deviceId: string;
  readonly historyDeliveryPubkeyB64Url: string;
}

/**
 * List the account's devices that have advertised a `history_delivery_pubkey`
 * — i.e. devices that recently recovered and are awaiting sealed peer history.
 * Returns `[]` on 404 / no advertisers. `GET /v1/devices/{accountId}` LISTS
 * (does not consume), so this is safe to poll.
 */
export async function listDevicesWithDeliveryKey(
  accountId: string,
): Promise<readonly DeviceWithDeliveryKey[]> {
  const trimmed = accountId.trim();
  if (trimmed.length === 0) return [];
  // The directory keys on the 16-byte account id. A 32-byte MLS UserId (from an
  // MLS roster) is a different id space and 400s — skip it instead of spamming.
  try {
    if (urlB64NoPadToBytes(trimmed).length !== 16) return [];
  } catch {
    return [];
  }
  let resp: DeviceDiscoverResponseWire;
  try {
    resp = await get<DeviceDiscoverResponseWire>(
      `/v1/devices/${encodeURIComponent(trimmed)}`,
    );
  } catch (err) {
    if (err instanceof NetworkError && err.status === 404) return [];
    throw err;
  }
  const out: DeviceWithDeliveryKey[] = [];
  for (const row of resp.devices) {
    const pub = row.history_delivery_pubkey;
    if (typeof pub === 'string' && pub.length > 0) {
      out.push({ deviceId: row.device_id, historyDeliveryPubkeyB64Url: pub });
    }
  }
  return out;
}

/**
 * List the HEX device ids of every device on `accountId` (this device + linked
 * siblings). `GET /v1/devices/{accountId}` LISTS (does not consume KPs), so it's
 * safe to call on bootstrap. Hex form matches the `senderAccountId` the sync
 * engine derives from `sender_device` (`bytesToHex`), so self-attribution can
 * recognise a sibling device's message as our own. Returns `[]` on 404 / error.
 */
export async function listSelfDeviceIdsHex(
  accountId: string,
): Promise<readonly string[]> {
  const trimmed = accountId.trim();
  if (trimmed.length === 0) return [];
  try {
    if (urlB64NoPadToBytes(trimmed).length !== 16) return [];
  } catch {
    return [];
  }
  let resp: DeviceDiscoverResponseWire;
  try {
    resp = await get<DeviceDiscoverResponseWire>(
      `/v1/devices/${encodeURIComponent(trimmed)}`,
      { headers: { 'X-Ping-Skip-Reauth': '1' } },
    );
  } catch (err) {
    if (err instanceof NetworkError && err.status === 404) return [];
    return [];
  }
  const out: string[] = [];
  for (const row of resp.devices) {
    try {
      out.push(bytesToHex(urlB64NoPadToBytes(row.device_id)).toLowerCase());
    } catch {
      // skip malformed device id
    }
  }
  return out;
}

/**
 * Resolve which member ACCOUNT (of those passed) owns `deviceIdB64Url`, by
 * listing each account's devices and matching the device id. Used by the
 * recovery drain to find the sender account of an inbound history transfer so
 * its pinned identity pubkey can VERIFY the sealed bundle. Returns `null` when
 * no candidate account claims the device.
 */
export async function findAccountForDevice(params: {
  candidateAccountIds: readonly string[];
  deviceIdB64Url: string;
}): Promise<string | null> {
  const target = params.deviceIdB64Url;
  for (const accountId of params.candidateAccountIds) {
    let resp: DeviceDiscoverResponseWire;
    try {
      resp = await get<DeviceDiscoverResponseWire>(
        `/v1/devices/${encodeURIComponent(accountId)}`,
      );
    } catch {
      continue; // transient / 404 — try the next candidate
    }
    if (resp.devices.some((d) => d.device_id === target)) return accountId;
  }
  return null;
}

/**
 * Fetch an account's pinned user-identity Ed25519 pubkey (base64url) from its
 * profile — the key that VERIFIES sealed history bundles it signed. Returns
 * `null` when the field is absent (older account) or the profile 404s.
 */
export async function getUserIdentityPubkey(accountId: string): Promise<string | null> {
  const trimmed = accountId.trim();
  if (trimmed.length === 0) return null;
  if (/^[0-9a-fA-F]{32,}$/.test(trimmed)) return null;
  try {
    const w = await get<{ user_identity_pubkey?: string | null }>(
      `/v1/users/${encodeURIComponent(trimmed)}/profile`,
      { headers: { 'X-Ping-Skip-Reauth': '1' } },
    );
    const pub = w.user_identity_pubkey;
    return typeof pub === 'string' && pub.length > 0 ? pub : null;
  } catch (err) {
    if (err instanceof NetworkError && err.status === 404) return null;
    throw err;
  }
}

export async function claimContactByAccountId(params: {
  accountId: string;
}): Promise<ClaimedContact> {
  const trimmed = params.accountId.trim();
  if (trimmed.length === 0) {
    throw new Error('claimContactByAccountId: empty accountId');
  }
  // `POST /v1/contacts/claim` is atomic — backed by the same
  // `ClaimActiveKeyPackageForUser` SQL primitive as the 3PID
  // `/v1/contacts/lookup` path (`UPDATE ... RETURNING` with
  // `FOR UPDATE SKIP LOCKED`). Two concurrent peers calling this with
  // the same `account_id` receive DIFFERENT KeyPackages, which is
  // what MLS depends on — every Welcome must reference a single-use
  // InitKey or the recipient drops the second one with
  // "No matching key package was found in the key store".
  //
  // The previous fallback to `GET /v1/devices/{accountId}` (list, no
  // consume) caused exactly that symptom in production — see the
  // log session captured 2026-05-22.
  try {
    const resp = await post<ContactsLookupWire>('/v1/contacts/claim', {
      account_id: trimmed,
    });
    return {
      accountId: resp.account_id,
      deviceId: resp.device_id,
      keyPackage: base64urlToBytes(resp.key_package),
    };
  } catch (err) {
    if (err instanceof NetworkError) {
      // The BE 404s with `key_package_pool_empty` (HTTP 410 Gone) when
      // the target's pool has no unconsumed KPs. Surface it the same
      // way the legacy multi-device path did so the UI's existing
      // pool-empty messaging keeps working without changes.
      if (err.status === 410 && err.serverError === 'key_package_pool_empty') {
        throw new NetworkError('http', 404, undefined, {
          error: 'key_package_pool_empty',
          message:
            "That account isn't ready to receive a chat yet. Ask them to sign in again — their device needs to publish encryption keys.",
        });
      }
    }
    throw err;
  }
}

/**
 * Multi-device variant of [[claimContactByAccountId]]. Returns one
 * entry per active device the target has on file. Use when the caller
 * intends to invite the contact's whole fleet into an MLS group (the
 * canonical chat-bootstrap path per
 * docs/architecture/multi-device.md "Per-device fan-out").
 *
 * One key package per device is consumed lazily — the BE returns
 * pre-claimed bytes for each device in a single round trip.
 *
 * Errors:
 *   - 404 `key_package_pool_empty` — target has no device with active
 *     KPs. UI should suggest they re-sign-in.
 *   - 404 `no_active_devices` — empty response array (defensive; the
 *     BE returns 404 directly in this case, but we double-guard).
 *
 * Devices are returned newest-first by `last_seen_at` so the live
 * device (most likely to consume the Welcome before timeout) wins
 * placement at index 0 — relevant for older single-device callers
 * that still use [[claimContactByAccountId]] under the hood.
 */
interface ClaimFanoutResponseWire {
  readonly account_id: string;
  readonly devices: ReadonlyArray<{
    readonly device_id: string;
    readonly key_package: string;
  }>;
}

export async function claimAllDevicesByAccountId(params: {
  accountId: string;
}): Promise<ClaimedContactMultiDevice> {
  const trimmed = params.accountId.trim();
  if (trimmed.length === 0) {
    throw new Error('claimAllDevicesByAccountId: empty accountId');
  }
  // `POST /v1/contacts/claim-fanout` — atomic CONSUME one KP per
  // active device on the account. Replaces the legacy
  // `GET /v1/devices/{accountId}` (list, no consume) which returned
  // the SAME KP to concurrent inviters and caused the recipient to
  // drop the second Welcome with "No matching key package was found
  // in the key store". See the prod log on 2026-05-22 for the
  // failure trace.
  let resp: ClaimFanoutResponseWire;
  try {
    resp = await post<ClaimFanoutResponseWire>('/v1/contacts/claim-fanout', {
      account_id: trimmed,
    });
  } catch (err) {
    if (err instanceof NetworkError) {
      if (err.status === 410 && err.serverError === 'key_package_pool_empty') {
        throw new NetworkError('http', 404, undefined, {
          error: 'key_package_pool_empty',
          message:
            "That account isn't ready to receive a chat yet. Ask them to sign in again — their device needs to publish encryption keys.",
        });
      }
      if (err.status === 404 && err.serverError === 'account_not_found') {
        throw new NetworkError('http', 404, undefined, {
          error: 'no_active_devices',
          message: "That account doesn't have any reachable devices right now.",
        });
      }
    }
    throw err;
  }
  if (resp.devices.length === 0) {
    throw new NetworkError('http', 404, undefined, {
      error: 'no_active_devices',
      message: "That account doesn't have any reachable devices right now.",
    });
  }
  const devices: ClaimedContactDevice[] = resp.devices.map((row) => ({
    deviceId: row.device_id,
    keyPackage: base64urlToBytes(row.key_package),
  }));
  return { accountId: resp.account_id, devices };
}

/** One peer account's devices, shaped for `createGroupConversation`. */
export interface ClaimedGroupMemberDevices {
  readonly deviceIdHex: string;
  readonly keyPackageBytes: Uint8Array;
}

/**
 * Claim every active device for one group invitee (`claim-fanout`).
 * Injected into `@ping/ui` `createGroupConversation` as
 * `claimDevicesForAccount`.
 */
export async function claimDevicesForGroupMember(
  accountId: string,
): Promise<readonly ClaimedGroupMemberDevices[]> {
  const claimed = await claimAllDevicesByAccountId({ accountId });
  return claimed.devices.map((d) => ({
    deviceIdHex: d.deviceId,
    keyPackageBytes: d.keyPackage,
  }));
}

/**
 * List active device ids for an account without consuming KeyPackages.
 * Used to backfill group roster maps (device hex → display name).
 */
export async function listAccountDeviceIds(
  accountId: string,
): Promise<readonly string[]> {
  const trimmed = accountId.trim();
  if (trimmed.length === 0) {
    return [];
  }
  let resp: DeviceDiscoverResponseWire;
  try {
    // Decorative roster backfill — never let a 401 (e.g. fired during the
    // pre-bootstrap window before the token is set) trigger the global reauth /
    // logout. Degrade to an empty list instead.
    resp = await get<DeviceDiscoverResponseWire>(
      `/v1/devices/${encodeURIComponent(trimmed)}`,
      { headers: { 'X-Ping-Skip-Reauth': '1' } },
    );
  } catch (err) {
    if (err instanceof NetworkError && (err.status === 404 || err.status === 401)) {
      return [];
    }
    throw err;
  }
  const seen = new Set<string>();
  const out: string[] = [];
  for (const row of resp.devices) {
    const canon = canonicalDeviceIdHex(row.device_id);
    if (canon !== null && !seen.has(canon)) {
      seen.add(canon);
      out.push(canon);
    }
  }
  return out;
}

// `listKeyPackagesForLinkedDevice` (the legacy non-consuming `GET
// /v1/devices/{id}` LIST used to claim multiple KeyPackages for one device) was
// removed: both the linked-device reconciler and recovered-peer re-admit now
// claim atomically via `claimAdmissionKeyPackages`
// (`POST /v1/devices/{device_id}/admission-keypackages`), which consumes
// single-use KeyPackages and pads with the last-resort — so concurrent admitters
// can't hand the same KeyPackage to two chats.

/**
 * The caller device's current active single-use KeyPackage pool size
 * (`GET /v1/key-packages/count`). Drives low-watermark replenish: when this
 * drops below the client threshold, the device uploads a fresh batch so a peer
 * can always invite/admit it. Returns `null` on any error (caller treats a
 * missing count as "don't know — skip this top-up tick").
 */
export async function getKeyPackageCount(): Promise<number | null> {
  try {
    const resp = await get<{ count?: number }>('/v1/key-packages/count');
    return typeof resp.count === 'number' ? resp.count : null;
  } catch {
    return null;
  }
}

/**
 * Atomically CLAIM up to `count` KeyPackages for a freshly-linked sibling
 * `deviceId`, one per existing conversation to admit it into. Unlike
 * `listKeyPackagesForLinkedDevice` (the legacy non-consuming LIST), this
 * CONSUMES distinct single-use KeyPackages server-side — so two siblings
 * draining the admission ledger concurrently can never hand the same KP to two
 * chats (the "No matching key package" Welcome drop). When the single-use pool
 * can't cover `count`, the BE pads with the device's reusable last-resort
 * KeyPackage, so an account with more conversations than the new device's pool
 * size no longer leaves the surplus chats permanently un-admitted.
 *
 * `POST /v1/devices/{device_id}/admission-keypackages` → up to `count` KPs.
 * Returns `[]` on 404 (device not found / no active account).
 */
export async function claimAdmissionKeyPackages(params: {
  deviceIdBase64Url: string;
  count: number;
}): Promise<Uint8Array[]> {
  const deviceId = params.deviceIdBase64Url.trim();
  if (deviceId.length === 0) {
    throw new Error('claimAdmissionKeyPackages: empty deviceId');
  }
  if (params.count <= 0) return [];
  let resp: { key_packages?: string[] };
  try {
    resp = await post<{ key_packages?: string[] }>(
      `/v1/devices/${encodeURIComponent(deviceId)}/admission-keypackages`,
      { count: params.count },
    );
  } catch (err) {
    if (err instanceof NetworkError && err.status === 404) {
      return [];
    }
    throw err;
  }
  return (resp.key_packages ?? []).map((kp) => base64urlToBytes(kp));
}

function base64urlToBytes(s: string): Uint8Array {
  // `auth.EncodeB64URL` on the BE uses `base64.RawURLEncoding`
  // (alphabet `-_`, no padding). Restore to standard alphabet +
  // padding before handing to `atob`.
  const padding = '='.repeat((4 - (s.length % 4)) % 4);
  const standard = (s + padding).replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(standard);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

// `WITH_MOCK` is intentionally not consulted by `lookupUser` /
// `claimContact`: the start-chat path is a no-mocks live-only flow
// (it claims a KeyPackage server-side that we can't fake).

// Heuristic gate for `lookupUser` — only fire the request when the
// input could plausibly match an `auth.accounts.primary_3pid`. The
// BE 404s on every other shape, so without this gate every
// keystroke in the new-chat search box lights up a red network
// entry in devtools.
//   • Email: stricter than RFC 5322 but covers the realistic
//     shape we expect users to type (`<local>@<host>.<tld>`).
//   • Phone: E.164 — leading `+`, 7-15 digits, no separators. We
//     accept 7+ to keep the gate non-blocking for short test
//     numbers; production phones are 10+.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const E164_RE = /^\+\d{7,15}$/;

function looksLikeCompleteIdentifier(input: string): boolean {
  return EMAIL_RE.test(input) || E164_RE.test(input);
}
